import { prefersReducedMotion } from "@/lib/motion";

/**
 * A photograph with a digital layer on top of real objects in it.
 *
 * The frame always keeps the image's own aspect ratio, so every hotspot and
 * SVG coordinate is expressed in image pixels and lands on the same object at
 * every viewport. Cropping is done by scaling and shifting the frame inside an
 * overflow-hidden box, never by distorting the image.
 *
 * Layers, bottom to top: <picture>, SVG lines and streams, HTML hotspots and
 * whatever children the scene places (notification cards, panels).
 *
 * Visibility of each element is a plain integer: it turns on once `stage`
 * reaches its `on` value. CSS does the transitions.
 */
const SRC = {
  office: { w: 1024, h: 768 },
  owner: { w: 1024, h: 768 },
  call: { w: 1024, h: 768 },
  desk: { w: 1024, h: 1024 },
};

export function pct(v, total) {
  return `${(v / total) * 100}%`;
}

export default function LivingPhoto({
  name,
  alt,
  stage = 0,
  inView = true,
  hotspots = [],
  paths = [],
  hub,
  frame = { scale: 1, x: 0, y: 0 },
  ratio,
  priority = false,
  className = "",
  children,
}) {
  const { w, h } = SRC[name];
  const reduced = prefersReducedMotion();
  const aspect = ratio || `${w} / ${h}`;

  return (
    <div className={`lp ${className}`} style={{ aspectRatio: aspect }} data-stage={stage}>
      <div
        className="lp__frame"
        style={{
          aspectRatio: `${w} / ${h}`,
          width: `${(frame.scale || 1) * 100}%`,
          left: `${frame.x || 0}%`,
          top: `${frame.y || 0}%`,
        }}
      >
        <picture>
          <source type="image/webp" srcSet={`/img/${name}-640.webp 640w, /img/${name}-1024.webp 1024w`} sizes="(max-width: 767px) 100vw, 60vw" />
          <img
            src={`/img/${name}-1024.jpg`}
            alt={alt}
            width={w}
            height={h}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            decoding="async"
            className="lp__img"
          />
        </picture>

        <svg className="lp__svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
          {paths.map((p) => (
            <path
              key={p.id}
              id={`${name}-${p.id}`}
              d={p.d}
              pathLength="1"
              className={`lp__line ${p.className || ""}`}
              data-on={stage >= p.on}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {!reduced && inView && paths.filter((p) => p.stream && stage >= p.stream).map((p) => (
            <g key={`s-${p.id}`}>
              {[0, 1].map((i) => (
                <circle key={i} r="4" className="lp__dot">
                  <animateMotion dur={`${p.dur || 2.6}s`} begin={`${i * ((p.dur || 2.6) / 2) + (p.delay || 0)}s`} repeatCount="indefinite" calcMode="linear">
                    <mpath href={`#${name}-${p.id}`} />
                  </animateMotion>
                </circle>
              ))}
            </g>
          ))}
        </svg>

        {hotspots.map((s) => (
          <div
            key={s.id}
            className={`lp__spot ${s.side === "left" ? "lp__spot--left" : ""} ${s.className || ""}`}
            style={{ left: pct(s.x, w), top: pct(s.y, h) }}
            data-on={stage >= s.on}
            data-done={s.done != null && stage >= s.done}
          >
            <span className="lp__ring" />
            <span className="lp__tag">
              <b>{s.label}</b>
              {s.sub && <i>{s.sub}</i>}
            </span>
          </div>
        ))}

        {hub && (
          <div className="lp__hub" style={{ left: pct(hub.x, w), top: pct(hub.y, h) }} data-on={stage >= hub.on}>
            <span className="lp__hub-core">{hub.label || "AUT labs"}</span>
            {hub.sub && <span className="lp__hub-sub">{hub.sub}</span>}
          </div>
        )}

        {children}
      </div>
    </div>
  );
}

/** A small HTML element pinned to an object in the photo, e.g. a notification. */
export function Pin({ name, x, y, on, stage, off, className = "", children }) {
  const { w, h } = SRC[name];
  return (
    <div
      className={`lp__pin ${className}`}
      style={{ left: pct(x, w), top: pct(y, h) }}
      data-on={stage >= on && (off == null || stage < off)}
    >
      {/* Below the canAnimateHeavy() breakpoint the full card is hidden by CSS
          (too many of them collide on a narrow photo) and only this dot marks
          the spot; the card's content moves into a <PhotoLegend> instead. */}
      <span className="lp__pin-dot" aria-hidden="true" />
      {children}
    </div>
  );
}

/**
 * Mobile-only companion list for a photo's hotspots or pins. Below the
 * canAnimateHeavy() breakpoint, several tags/cards anchored close together on
 * a narrow photo have nowhere to go but on top of each other, so their text
 * moves here — a normal, non-overlapping list — while the photo keeps only a
 * small dot marking where each one lives. Hidden by CSS at that breakpoint
 * and above, where the on-photo tags/cards already work.
 */
export function PhotoLegend({ items, stage, label }) {
  return (
    <ol className="lp-legend" aria-label={label}>
      {items.map((it) => (
        <li
          key={it.id}
          className="lp-legend__row"
          data-on={stage >= it.on}
          data-done={it.done != null && stage >= it.done}
        >
          <span className="lp-legend__dot" aria-hidden="true" />
          <span className="lp-legend__text">
            <b>{it.title}</b>
            {it.sub && <span>{it.sub}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}
